import type { DataRow, SubmissionMode } from '@/types';

export interface TitleBatchItem { id: string; title: string }
export interface SkuBatchItem { row: number; values: Record<string, string> }
export interface SkuWeightBatchItem { row: number; skuName: string; skuImages: string[] }
export interface SkuProductBatchInput { productId: string; rows: SkuBatchItem[] }
export interface SkuWeightProductBatchInput { productId: string; productName: string; skus: SkuWeightBatchItem[] }

function clean(text: string): string {
    return text.trim().replace(/^```(?:json|csv)?\s*/i, '').replace(/\s*```$/i, '').trim();
}

function parseJson(text: string): unknown {
    const value = clean(text);
    try { return JSON.parse(value); } catch {
        const starts = [value.indexOf('['), value.indexOf('{')].filter(index => index >= 0);
        const start = starts.length > 0 ? Math.min(...starts) : -1;
        const end = Math.max(value.lastIndexOf(']'), value.lastIndexOf('}'));
        if (start >= 0 && end > start) return JSON.parse(value.slice(start, end + 1));
        throw new Error('模型返回的 JSON 格式无效');
    }
}

function arrayFrom(value: unknown, keys = ['results', 'data', 'items', 'rows', 'skus']): unknown[] {
    if (Array.isArray(value)) return value;
    if (value && typeof value === 'object') {
        const object = value as Record<string, unknown>;
        for (const key of keys) if (Array.isArray(object[key])) return object[key] as unknown[];
    }
    throw new Error('模型返回中没有结果数组');
}

function text(value: unknown): string { return value == null ? '' : String(value).trim(); }

export function csvEscape(value: unknown): string {
    const valueText = text(value);
    return /[",\r\n]/.test(valueText) ? `"${valueText.replace(/"/g, '""')}"` : valueText;
}

function parseCsv(value: string): string[][] {
    const input = clean(value).replace(/^\uFEFF/, '');
    const rows: string[][] = [];
    let row: string[] = [], cell = '', quoted = false;
    for (let index = 0; index < input.length; index++) {
        const char = input[index];
        if (quoted) {
            if (char === '"' && input[index + 1] === '"') { cell += '"'; index++; }
            else if (char === '"') quoted = false;
            else cell += char;
        } else if (char === '"' && cell === '') quoted = true;
        else if (char === ',') { row.push(cell); cell = ''; }
        else if (char === '\n' || char === '\r') {
            if (char === '\r' && input[index + 1] === '\n') index++;
            row.push(cell); cell = '';
            if (row.some(item => item !== '')) rows.push(row);
            row = [];
        } else cell += char;
    }
    if (cell !== '' || row.length > 0) { row.push(cell); if (row.some(item => item !== '')) rows.push(row); }
    return rows;
}

function systemPrompt(prompt: string, format: string): string {
    return `${prompt}\n\n【批量返回格式】\n${format}\n只返回规定格式，不要解释、Markdown 或额外文字。`;
}

export function buildTitleBatchRequest(mode: SubmissionMode, prompt: string, items: TitleBatchItem[]) {
    const rules = `${prompt}\n每个商品必须独立处理；输入有多少项就返回多少项，id 必须原样返回且只能出现一次。`;
    if (mode === 'json' || mode === 'batch-json') return {
        system: systemPrompt(rules, '返回 JSON 数组，每项为 {"id":"输入id","title":"处理后的标题"}。'),
        user: JSON.stringify(items),
    };
    return {
        system: systemPrompt(rules, '返回 CSV，表头必须为 id,optimized_title。'),
        user: ['id,title', ...items.map(item => `${csvEscape(item.id)},${csvEscape(item.title)}`)].join('\n'),
    };
}

export function parseTitleBatchResponse(mode: SubmissionMode, value: string): Array<{ id: string; title: string }> {
    if (mode === 'json' || mode === 'batch-json') return arrayFrom(parseJson(value)).map(item => {
        const row = (item ?? {}) as Record<string, unknown>;
        return { id: text(row.id), title: text(row.title ?? row.optimized_title ?? row.result) };
    }).filter(item => item.id && item.title);
    const rows = parseCsv(value), headers = rows[0]?.map(item => item.trim().toLowerCase()) ?? [];
    const id = headers.indexOf('id');
    const title = headers.includes('optimized_title') ? headers.indexOf('optimized_title') : headers.indexOf('title');
    if (id < 0 || title < 0) throw new Error('模型返回的 CSV 缺少 id 或 optimized_title 列');
    return rows.slice(1).map(row => ({ id: text(row[id]), title: text(row[title]) })).filter(item => item.id && item.title);
}

export function buildSkuRows(rows: DataRow[], indices: number[], fields: string[], skipShort = false, threshold = 0): SkuBatchItem[] {
    return indices.map(row => ({
        row,
        values: Object.fromEntries(fields.map(field => {
            const value = text(rows[row]?.[field]);
            return [field, skipShort && value.length <= threshold ? '' : value];
        })),
    })).filter(item => fields.some(field => item.values[field]));
}

export function buildSkuProductsBatchRequest(prompt: string, products: SkuProductBatchInput[], fields: string[]) {
    return {
        system: systemPrompt(`${prompt}\n需要处理的字段：${fields.join(', ')}。每个商品和 SKU 必须独立处理。`, '返回 {"products":[{"productId":"原值","skus":[{"row":输入行号,字段名:"优化后值"}]}]}，完整保留 productId 和 row。'),
        user: JSON.stringify({ products: products.map(product => ({ productId: product.productId, skus: product.rows.map(row => ({ row: row.row, ...row.values })) })) }),
    };
}

export function buildSkuProductRequest(mode: SubmissionMode, prompt: string, productId: string, rows: SkuBatchItem[], fields: string[]) {
    if (mode === 'csv') {
        const header = ['row', ...fields].join(',');
        return {
            system: systemPrompt(`${prompt}\n需要处理的字段：${fields.join(', ')}。`, `返回 CSV，表头必须为 ${header}，row 必须原样保留。`),
            user: [header, ...rows.map(item => [item.row, ...fields.map(field => item.values[field] ?? '')].map(csvEscape).join(','))].join('\n'),
        };
    }
    return buildSkuProductsBatchRequest(prompt, [{ productId, rows }], fields);
}

export function parseSkuProductResponse(mode: SubmissionMode, value: string, fields: string[]): SkuBatchItem[] {
    if (mode !== 'csv') return parseSkuProductsBatchResponse(value, fields)[0]?.skus ?? [];
    const rows = parseCsv(value), headers = rows[0]?.map(item => item.trim()) ?? [];
    const rowIndex = headers.indexOf('row');
    if (rowIndex < 0) throw new Error('模型返回的 CSV 缺少 row 列');
    return rows.slice(1).map(row => ({
        row: Number(row[rowIndex]),
        values: Object.fromEntries(fields.map(field => [field, text(row[headers.indexOf(field)])]).filter(([, fieldValue]) => fieldValue)),
    })).filter(item => Number.isInteger(item.row) && Object.keys(item.values).length > 0);
}

export function parseSkuProductsBatchResponse(value: string, fields: string[]): Array<{ productId: string; skus: SkuBatchItem[] }> {
    return arrayFrom(parseJson(value), ['products']).map(item => {
        const product = (item ?? {}) as Record<string, unknown>;
        const skus = (Array.isArray(product.skus) ? product.skus : []).map(item => {
            const sku = (item ?? {}) as Record<string, unknown>;
            return { row: Number(sku.row), values: Object.fromEntries(fields.map(field => [field, text(sku[field])]).filter(([, value]) => value)) };
        }).filter(item => Number.isInteger(item.row) && Object.keys(item.values).length > 0);
        return { productId: text(product.productId), skus };
    }).filter(product => product.productId && product.skus.length > 0);
}

export function buildSkuWeightProductsBatchRequest(prompt: string, products: SkuWeightProductBatchInput[]) {
    return {
        system: systemPrompt(`${prompt}\n每个商品和 SKU 必须独立估算。`, '返回 {"products":[{"productId":"原值","skus":[{"row":输入行号,"weight":重量数值}]}]}，重量单位为 Kg。'),
        user: JSON.stringify({ products }),
    };
}

export function buildSkuWeightProductRequest(mode: SubmissionMode, prompt: string, product: SkuWeightProductBatchInput) {
    if (mode === 'csv') return {
        system: systemPrompt(prompt, '返回 CSV，表头必须为 row,weight，重量单位为 Kg，row 必须原样保留。'),
        user: ['row,product_name,sku_name,sku_images', ...product.skus.map(item => [item.row, product.productName, item.skuName, item.skuImages.join('|')].map(csvEscape).join(','))].join('\n'),
    };
    return buildSkuWeightProductsBatchRequest(prompt, [product]);
}

export function parseSkuWeightProductResponse(mode: SubmissionMode, value: string): Array<{ row: number; weight: string }> {
    if (mode !== 'csv') return parseSkuWeightProductsBatchResponse(value)[0]?.skus ?? [];
    const rows = parseCsv(value), headers = rows[0]?.map(item => item.trim().toLowerCase()) ?? [];
    const rowIndex = headers.indexOf('row'), weightIndex = headers.includes('weight') ? headers.indexOf('weight') : headers.indexOf('sku_weight');
    if (rowIndex < 0 || weightIndex < 0) throw new Error('模型返回的 CSV 缺少 row 或 weight 列');
    return rows.slice(1).map(row => ({ row: Number(row[rowIndex]), weight: text(row[weightIndex]) })).filter(item => Number.isInteger(item.row) && item.weight);
}

export function parseSkuWeightProductsBatchResponse(value: string): Array<{ productId: string; skus: Array<{ row: number; weight: string }> }> {
    return arrayFrom(parseJson(value), ['products']).map(item => {
        const product = (item ?? {}) as Record<string, unknown>;
        const skus = (Array.isArray(product.skus) ? product.skus : []).map(item => {
            const sku = (item ?? {}) as Record<string, unknown>;
            return { row: Number(sku.row), weight: text(sku.weight ?? sku.skuWeight ?? sku.result) };
        }).filter(item => Number.isInteger(item.row) && item.weight);
        return { productId: text(product.productId), skus };
    }).filter(product => product.productId && product.skus.length > 0);
}
